import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PATHS } from "@/routes/paths";
import { useUpdateAllNotificationAsReadMutation } from "@/services/notificationsApi";
import { CheckCheck, Settings2 } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface IProps {
  getAllNotificationRefetch: () => void;
  unreadCount: number;
  setOpen: (open: boolean) => void;
}

export const NotificationsHeader = ({
  getAllNotificationRefetch,
  unreadCount,
  setOpen,
}: IProps) => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const [updateMarkAllAsRead, { isLoading }] =
    useUpdateAllNotificationAsReadMutation();

  const handleMarkReadAll = async () => {
    const id = toast.loading("Processing...");
    await updateMarkAllAsRead()
      .unwrap()
      .then((res) => {
        getAllNotificationRefetch();
        toast.success(
          res.message ?? t("common:inbox.all_notifications_marked_read"),
          {
            id,
          },
        );
      })
      .catch((e) => {
        toast.error(
          e?.data?.message ??
            e.message ??
            t("common:inbox.failed_to_mark_as_read"),
          {
            id,
          },
        );
      });
  };

  const handleSettings = () => {
    navigate(PATHS.MY_SPACE.MS_SETTINGS_PATHS.TAB_NOTIFICATIONS);
    setOpen(false);
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
      <div className="flex items-center gap-2">
        <h3 className="text-[13px] font-semibold tracking-tight">
          {t("nav:modules.notifications")}
        </h3>
      </div>

      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                // Hover text comes from the TooltipContent below; this names the
                // icon-only button for screen readers, which the tooltip cannot.
                aria-label={t("common:a11y.mark_all_as_read")}
                className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
                disabled={isLoading || unreadCount === 0}
                onClick={handleMarkReadAll}
              >
                <CheckCheck size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {t("common:inbox.mark_all_as_read")}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("nav:tabs.notification_settings")}
                className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={handleSettings}
              >
                <Settings2 size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {t("common:inbox.notification_settings")}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>
  );
};
